import React, { FC, memo } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import Icon from '~ComponentsImproved/Icon';
import ArrowIcon from '~Assets/icons/navigate-right.svg';
import { useButton, useI18n } from '~Hooks';
import { TransparentButton } from '~Styles/buttons';

const slideDown = keyframes`
  from {
    transform: translateY(-100%);
  }
  to {
    transform: translateY(0);
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-flow: nowrap;
  overflow: auto;
  width: 100%;
  box-sizing: border-box;
  height: 48px;
  padding: 4px 8px;
  white-space: nowrap;
  background-color: var(--colorBlueGray1200);
  border-top: 1px solid var(--colorBlueGray1100);
  animation: ${slideDown} 200ms ease-out;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;

  & > div {
    margin: 0 4px;
  }
`;

const Button = styled(TransparentButton)<{ current: boolean }>`
  border-radius: 4px;
  border: 1px solid;
  border-color: ${({ current }) =>
    current ? 'var(--colorBlueGray600)' : 'var(--colorBlueGray400)'};
  text-transform: none;
  color: ${({ current }) => (current ? 'var(--colorBlueGray600)' : 'var(--colorWhite)')};
  max-width: 220px;
  min-width: unset;
  height: 26px;
  min-height: unset;
  padding: 0 8px;
  font-size: 11px;
  pointer-events: ${({ current }) => (current ? 'none' : 'auto')};

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
  }
`;

interface CrumbButtonProps {
  crumb: { id: string; name: string };
  current: boolean;
  onClick: () => void;
}

const CrumbButton: FC<CrumbButtonProps> = memo(({ crumb, current, onClick }) => {
  const { i18n } = useI18n();
  const btn = useButton({
    onClick: () => onClick(),
  });

  return (
    <Button {...btn} current={current} size="small">
      <span>{i18n(crumb.name) ?? crumb.name}</span>
    </Button>
  );
});

export interface Props {
  breadcrumbs: { id: string; name: string }[];
  handleClick: (index: number) => void;
}

/** Non-absolute breadcrumb bar for Handy, flows inline instead of overlaying the layout like the POS version. */
const HandyBreadcrumbs: FC<Props> = memo(({ breadcrumbs, handleClick }) => {
  return (
    <Wrapper>
      {breadcrumbs.map((crumb, i) => (
        <ButtonWrapper key={crumb.id}>
          <CrumbButton
            crumb={crumb}
            current={breadcrumbs.length === i + 1}
            onClick={() => handleClick(i)}
          />
          {breadcrumbs.length > i + 1 && <Icon src={ArrowIcon} height="12px" width="12px" />}
        </ButtonWrapper>
      ))}
    </Wrapper>
  );
});

export default HandyBreadcrumbs;
